(() => {
  'use strict';

  if (window.StoreInfiniteScroll?.booted) return;

  const SELECTORS = {
    section: '[data-section-type="collection"]',
    outer: '[data-products-grid]',
    grid: '#CollectionLoop',
    card: '[data-grid-item]',
    pagination: '.pagination-custom',
    next: '.pagination-custom__next[href]'
  };

  const MESSAGES = {
    loading: 'Učitavanje dodatnih proizvoda…',
    loaded: 'Učitano je još proizvoda.',
    retry: 'Pokušaj ponovo'
  };

  class InfiniteCollection {
    constructor(section) {
      this.section = section;
      this.outer = null;
      this.grid = null;
      this.pagination = null;
      this.sentinel = null;
      this.status = null;
      this.retryButton = null;
      this.observer = null;
      this.mutationObserver = null;
      this.controller = null;
      this.loading = false;
      this.destroyed = false;
      this.nextUrl = null;
      this.loadedUrls = new Set();
      this.productKeys = new Set();
      this.rebindTimer = null;
      this.boundRetry = () => this.loadNext(true);
      this.bind();
      this.watchForGridReplacement();
    }

    bind() {
      if (this.destroyed) return;
      const outer = this.section.querySelector(SELECTORS.outer);
      const grid = outer?.querySelector(SELECTORS.grid);
      if (!outer || !grid) return;

      this.abort();
      this.disconnectObserver();
      this.removeUi();

      this.outer = outer;
      this.grid = grid;
      this.pagination = outer.querySelector(SELECTORS.pagination);
      this.loading = false;
      this.loadedUrls.clear();
      this.productKeys.clear();
      this.collectExistingKeys();
      this.nextUrl = this.getNextUrl(outer, window.location.href);

      this.createUi();
      this.setPaginationEnhanced(true);

      if (this.nextUrl) this.observe();
      else this.finish();
    }

    watchForGridReplacement() {
      this.mutationObserver = new MutationObserver(() => {
        if (this.destroyed) return;
        const currentOuter = this.section.querySelector(SELECTORS.outer);
        const currentGrid = currentOuter?.querySelector(SELECTORS.grid);
        if (currentOuter === this.outer && currentGrid === this.grid) return;
        clearTimeout(this.rebindTimer);
        this.rebindTimer = window.setTimeout(() => this.bind(), 40);
      });
      this.mutationObserver.observe(this.section, { childList: true, subtree: true });
    }

    createUi() {
      this.status = document.createElement('div');
      this.status.className = 'store-infinite-scroll__status';
      this.status.setAttribute('aria-live', 'polite');
      this.status.setAttribute('aria-atomic', 'true');

      this.retryButton = document.createElement('button');
      this.retryButton.type = 'button';
      this.retryButton.className = 'store-infinite-scroll__retry';
      this.retryButton.textContent = MESSAGES.retry;
      this.retryButton.hidden = true;
      this.retryButton.addEventListener('click', this.boundRetry);

      this.sentinel = document.createElement('div');
      this.sentinel.className = 'store-infinite-scroll__sentinel';
      this.sentinel.setAttribute('aria-hidden', 'true');

      this.outer.append(this.status, this.retryButton, this.sentinel);
    }

    observe() {
      if (!this.sentinel || !this.nextUrl) return;
      this.observer = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) this.loadNext();
        },
        { root: null, rootMargin: '1000px 0px', threshold: 0 }
      );
      this.observer.observe(this.sentinel);
    }

    async loadNext() {
      if (this.destroyed || this.loading || !this.nextUrl) return;

      const requestUrl = this.normaliseUrl(this.nextUrl);
      if (!requestUrl || this.loadedUrls.has(requestUrl)) {
        this.finish();
        return;
      }

      this.loading = true;
      this.loadedUrls.add(requestUrl);
      this.controller = new AbortController();
      this.setStatus(MESSAGES.loading, true);
      this.retryButton.hidden = true;

      try {
        const response = await fetch(requestUrl, {
          signal: this.controller.signal,
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const nextDocument = new DOMParser().parseFromString(html, 'text/html');
        const nextOuter = nextDocument.querySelector(SELECTORS.outer);
        const nextGrid = nextOuter?.querySelector(SELECTORS.grid);
        if (!nextOuter || !nextGrid) throw new Error('Unexpected collection HTML');

        const cards = Array.from(nextGrid.querySelectorAll(`:scope > ${SELECTORS.card}`));
        if (!cards.length) {
          this.finish();
          return;
        }

        const fragment = document.createDocumentFragment();
        let inserted = 0;

        cards.forEach(card => {
          const key = this.cardKey(card);
          if (key && this.productKeys.has(key)) return;
          if (key) this.productKeys.add(key);

          card.querySelectorAll('img').forEach(image => {
            image.loading = 'lazy';
            image.decoding = 'async';
            if ('fetchPriority' in image) image.fetchPriority = 'low';
          });

          fragment.appendChild(card);
          inserted += 1;
        });

        if (!inserted) {
          this.finish();
          return;
        }

        this.grid.appendChild(fragment);
        this.nextUrl = this.getNextUrl(nextOuter, requestUrl);
        this.setStatus(MESSAGES.loaded, false);

        this.grid.dispatchEvent(new CustomEvent('store:infinite-scroll:loaded', {
          bubbles: true,
          detail: { count: inserted, url: requestUrl }
        }));
        document.dispatchEvent(new CustomEvent('theme:collection:reloaded', {
          detail: { container: this.grid, count: inserted }
        }));
        if (window.AOS?.refreshHard) window.AOS.refreshHard();

        if (!this.nextUrl || this.loadedUrls.has(this.normaliseUrl(this.nextUrl))) this.finish();
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('[StoreInfiniteScroll]', error);
        this.loadedUrls.delete(requestUrl);
        this.disconnectObserver();
        this.setStatus('', false);
        this.retryButton.hidden = false;
        this.setPaginationEnhanced(false);
      } finally {
        this.loading = false;
        this.controller = null;
      }
    }

    collectExistingKeys() {
      this.grid.querySelectorAll(`:scope > ${SELECTORS.card}`).forEach(card => {
        const key = this.cardKey(card);
        if (key) this.productKeys.add(key);
      });
    }

    cardKey(card) {
      if (card.id) {
        const match = card.id.match(/product-item--.+-(\d+)$/);
        if (match) return `id:${match[1]}`;
        return `element:${card.id}`;
      }
      const link = card.querySelector('[data-product-link], a[href*="/products/"]');
      if (!link) return null;
      const href = link.getAttribute('data-product-link') || link.getAttribute('href');
      try {
        const url = new URL(href, window.location.origin);
        return `url:${url.pathname.replace(/\/$/, '')}`;
      } catch (_) {
        return `url:${href}`;
      }
    }

    getNextUrl(root, baseUrl) {
      const href = root.querySelector(SELECTORS.next)?.getAttribute('href');
      if (!href) return null;
      try {
        const url = new URL(href, baseUrl);
        if (url.origin !== window.location.origin) return null;
        return url.href;
      } catch (_) {
        return null;
      }
    }

    normaliseUrl(value) {
      if (!value) return null;
      try {
        const url = new URL(value, window.location.href);
        url.hash = '';
        return url.href;
      } catch (_) {
        return null;
      }
    }

    setStatus(message, loading) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-loading', Boolean(loading));
    }

    setPaginationEnhanced(enhanced) {
      if (!this.pagination) return;
      this.pagination.classList.toggle('store-infinite-scroll__pagination--enhanced', enhanced);
    }

    finish() {
      this.nextUrl = null;
      this.disconnectObserver();
      this.setStatus('', false);
      if (this.retryButton) this.retryButton.hidden = true;
      this.setPaginationEnhanced(true);
      if (this.sentinel) this.sentinel.hidden = true;
    }

    abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.loading = false;
    }

    disconnectObserver() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
    }

    removeUi() {
      if (this.retryButton) this.retryButton.removeEventListener('click', this.boundRetry);
      this.status?.remove();
      this.retryButton?.remove();
      this.sentinel?.remove();
      this.status = null;
      this.retryButton = null;
      this.sentinel = null;
    }

    destroy() {
      this.destroyed = true;
      clearTimeout(this.rebindTimer);
      this.abort();
      this.disconnectObserver();
      this.mutationObserver?.disconnect();
      this.removeUi();
      this.setPaginationEnhanced(false);
    }
  }

  function addStyles() {
    if (document.getElementById('store-infinite-scroll-styles')) return;
    const style = document.createElement('style');
    style.id = 'store-infinite-scroll-styles';
    style.textContent = `
      .store-infinite-scroll__pagination--enhanced { position:absolute!important; width:1px!important; height:1px!important; padding:0!important; margin:-1px!important; overflow:hidden!important; clip:rect(0,0,0,0)!important; white-space:nowrap!important; border:0!important; }
      .store-infinite-scroll__sentinel { width:100%; height:1px; }
      .store-infinite-scroll__sentinel[hidden] { display:none!important; }
      .store-infinite-scroll__status { min-height:24px; padding:18px 12px 8px; text-align:center; font-size:.9em; opacity:.72; }
      .store-infinite-scroll__status:empty { display:none!important; min-height:0!important; padding:0!important; margin:0!important; }
      .store-infinite-scroll__status.is-loading::before { content:''; display:inline-block; width:14px; height:14px; margin-right:8px; border:1px solid currentColor; border-right-color:transparent; border-radius:50%; vertical-align:-2px; animation:storeInfiniteSpin .8s linear infinite; }
      .store-infinite-scroll__retry { display:block; margin:14px auto; padding:10px 16px; background:transparent; border:1px solid currentColor; cursor:pointer; }
      .store-infinite-scroll__retry[hidden] { display:none!important; }
      @keyframes storeInfiniteSpin { to { transform:rotate(360deg); } }
      @media (prefers-reduced-motion: reduce) { .store-infinite-scroll__status.is-loading::before { animation:none; } }
    `;
    document.head.appendChild(style);
  }

  function boot() {
    addStyles();
    const instances = [];
    document.querySelectorAll(SELECTORS.section).forEach(section => {
      if (section.dataset.storeInfiniteScroll === 'true') return;
      section.dataset.storeInfiniteScroll = 'true';
      instances.push(new InfiniteCollection(section));
    });
    window.StoreInfiniteScroll.instances = instances;
  }

  window.StoreInfiniteScroll = { booted: true, instances: [], boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
